// FE-SCRM-053 — Contacts tab top-level panel.
//
// Cross-prospect Sales Contact list. URL state is the canonical
// source for q / prospect_id / is_primary / archived / page / page_size;
// the FE-SCRM-060 shared components own those codecs.
//
// Vocabulary discipline: "Contacts" is the producer-side noun. No
// "leads" or "people" leaks.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import {
  Badge, Table, TBody, THead, Th, Td, Tr,
} from "@/design-system";
import {
  DashboardErrorState, DashboardLoadingState,
} from "@/features/dashboard";

import {
  CrmSearchInput, IncludeArchivedToggle, Pagination,
  TagsCell, readIncludeArchivedFromUrl, readPaginationFromUrl,
  readSearchFromUrl,
} from "../components";

import { useContactsListQuery } from "./hooks";
import type { ContactListQuery, SalesContact } from "./types";


export function ContactsListPanel(): JSX.Element {
  const [search, setSearch] = useSearchParams();

  const filters = useMemo<ContactListQuery>(() => {
    const q = readSearchFromUrl(search);
    const include_archived = readIncludeArchivedFromUrl(search);
    const pagination = readPaginationFromUrl(search);
    const is_primary_raw = search.get("is_primary");
    const is_primary =
      is_primary_raw === "true" ? true
        : is_primary_raw === "false" ? false : undefined;
    const out: ContactListQuery = {
      page: pagination.page,
      page_size: pagination.pageSize,
    };
    if (q) out.q = q;
    if (include_archived) out.include_archived = true;
    if (is_primary !== undefined) out.is_primary = is_primary;
    return out;
  }, [search]);

  const list_query = useContactsListQuery(filters);

  const rows = list_query.data?.rows ?? [];
  const total = list_query.data?.total ?? 0;

  function setIsPrimaryFilter(next: boolean | undefined) {
    const params = new URLSearchParams(search);
    params.delete("is_primary");
    if (next !== undefined) params.set("is_primary", String(next));
    params.delete("page");
    setSearch(params, { replace: false });
  }

  return (
    <div
      data-testid="contacts-list-panel"
      className="flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col">
          <h2 className="text-sm font-semibold text-text-primary">
            Contacts
          </h2>
          <p className="text-xs text-text-tertiary">
            Every person at every Prospect. Add new Contacts from
            the Prospect detail page.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <CrmSearchInput
          placeholder="Search name, email, phone, prospect…"
          className="w-72"
        />
        <PrimaryToggle
          value={filters.is_primary}
          onChange={setIsPrimaryFilter}
        />
        <IncludeArchivedToggle />
      </div>

      {list_query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : list_query.isError ? (
        <DashboardErrorState
          title="Couldn't load Contacts"
          description="Refresh to retry, or check the network connection."
          retry={() => list_query.refetch()}
        />
      ) : (
        <ContactsTable rows={rows} />
      )}

      <Pagination total={total} />
    </div>
  );
}


function PrimaryToggle({
  value, onChange,
}: {
  value: boolean | undefined;
  onChange: (next: boolean | undefined) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
      <span>Primary</span>
      <select
        data-testid="contacts-list-primary-filter"
        value={value === undefined ? "" : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(
            raw === "" ? undefined
              : raw === "true" ? true : false,
          );
        }}
        className={
          "rounded-md border border-gray-300 bg-white px-2 py-1 "
          + "text-sm focus:border-indigo-500 focus:outline-none "
          + "focus:ring-1 focus:ring-indigo-500"
        }
      >
        <option value="">All</option>
        <option value="true">Primary only</option>
        <option value="false">Non-primary only</option>
      </select>
    </label>
  );
}


function ContactsTable({ rows }: { rows: SalesContact[] }) {
  if (rows.length === 0) {
    return (
      <div
        data-testid="contacts-list-empty"
        className={
          "rounded-md border border-dashed border-gray-300 "
          + "bg-white p-8 text-center text-sm text-gray-500"
        }
      >
        No Contacts match these filters. Contacts are created from
        the Prospect detail page.
      </div>
    );
  }

  return (
    <Table dividers wrapperClassName="rounded-lg border border-border-subtle" data-testid="contacts-list-table">
      <THead className="border-b border-border-subtle bg-recessed text-xs uppercase tracking-wide text-text-secondary">
        <Tr>
          <Th>Name</Th>
          <Th>Title</Th>
          <Th>Email</Th>
          <Th>Phone</Th>
          <Th>Prospect</Th>
          <Th>Primary</Th>
          <Th>Tags</Th>
          <Th>Created</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => (
          <Tr
            key={row.contact_id}
            data-testid={`contacts-list-row-${row.contact_id}`}
            className="hover:bg-recessed"
          >
            <Td className="font-medium text-text-primary">
              <Link
                to={`/dashboard/sales-crm/contacts/${row.contact_id}`}
                className="text-teal hover:underline"
              >
                {row.display_name}
              </Link>
            </Td>
            <Td className="text-text-secondary">
              {row.title ?? <span className="text-text-tertiary">—</span>}
            </Td>
            <Td className="text-text-secondary">
              {row.email
                ? <a href={`mailto:${row.email}`} className="hover:underline">{row.email}</a>
                : <span className="text-text-tertiary">—</span>
              }
            </Td>
            <Td className="text-text-secondary">
              {row.phone ?? <span className="text-text-tertiary">—</span>}
            </Td>
            <Td className="text-text-secondary">
              {/* FE-SCRM-050 will own the prospect detail route; the
                  link is a safe stub until then. */}
              <Link
                to={`/dashboard/sales-crm/prospects/${row.prospect_id}`}
                className="text-teal hover:underline"
              >
                {row.prospect_name ?? "(unnamed prospect)"}
              </Link>
            </Td>
            <Td>
              {row.is_primary ? (
                <Badge variant="category">Primary</Badge>
              ) : (
                <span className="text-text-tertiary">—</span>
              )}
            </Td>
            <Td>
              {/* FE-SCRM-053: tag join tables for contacts ship
                  alongside FE-SCRM-055's tag library — until then,
                  the cell stays empty so the column reserves space. */}
              <TagsCell tags={[]} />
              <span className="text-text-tertiary">—</span>
            </Td>
            <Td className="text-text-tertiary text-xs">
              {row.created_at ? row.created_at.slice(0, 10) : "—"}
            </Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}
